import type React from 'react';
import { useCallback } from 'react';
import { hexToVec3 } from '../../utils';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { TextureShader } from '../../texture-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

// 1. WaterWaveShader Model
export type WaterWaveShaderConfig = BaseShaderConfig & {
  name: 'waterwave';
  duration: number;
  color: string;
  backgroundColor: string;
  amplitude: number;
  frequency: number;
  speed: number;
  steepness: number;
};
export class WaterWaveShader extends TextureShader {
  name = 'waterwave' as const;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;
  color!: string;
  backgroundColor!: string;
  amplitude!: number;
  frequency!: number;
  speed!: number;
  steepness!: number;

  constructor(props: Partial<WaterWaveShader> = {}) {
    super();

    // Set defaults
    this.name = 'waterwave';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;
    this.color = '#0077ff';
    this.backgroundColor = '#000000';
    this.amplitude = 0.1;
    this.frequency = 10.0;
    this.speed = 1.0;
    this.steepness = 0.5;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
            precision mediump float;
            ${this.getTextureUniforms()}
            uniform vec3 u_color;
            uniform vec3 u_background_color;
            uniform float u_amplitude;
            uniform float u_frequency;
            uniform float u_speed;
            uniform float u_steepness;

            vec2 gerstner_wave(vec2 pos, float w, float A, float S, vec2 D, float phase_offset) {
                float dot_d_p = dot(D, pos);
                float phase = S * u_time + phase_offset;
                float cos_val = cos(w * dot_d_p + phase);
                float sin_val = sin(w * dot_d_p + phase);
                pos.x += u_steepness * A * D.x * cos_val;
                pos.y += u_steepness * A * D.y * cos_val;
                return pos;
            }

            void main() {
                ${this.getTextureUVPreamble()}

                // If we're outside the reference bounds, show background
                if (isOutOfBounds) {
                    gl_FragColor = vec4(u_background_color, 1.0);
                    return;
                }

                float t = u_time;

                vec2 p = uv;

                // Multiple wave directions for more realistic water
                p = gerstner_wave(p, u_frequency, u_amplitude, u_speed, normalize(vec2(1.0, 0.2)), 0.0);
                p = gerstner_wave(p, u_frequency * 0.7, u_amplitude * 0.6, u_speed * 0.8, normalize(vec2(-0.8, 0.6)), 1.5);
                p = gerstner_wave(p, u_frequency * 1.3, u_amplitude * 0.4, u_speed * 1.2, normalize(vec2(0.3, -1.0)), 3.0);

                // Add foam and wave crests
                float waveHeight = 1.0 - p.y;
                float foam = smoothstep(0.85, 1.0, waveHeight) * 0.5;

                // Enhanced color mixing with depth and foam
                vec3 waterColor = mix(u_background_color, u_color, waveHeight);
                vec3 foamColor = mix(waterColor, vec3(1.0), foam);

                // Add subtle shimmer effect
                float shimmer = sin(p.x * 50.0 + t * 5.0) * 0.1 + 0.9;
                foamColor *= shimmer;

                gl_FragColor = vec4(foamColor, 1.0);
            }
        `;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'speed',
        label: 'Wave Speed',
        description: 'Speed of wave movement',
        min: 0.1,
        max: 5,
        step: 0.1,
        defaultValue: 1.0,
        colspan: 1,
      },
      {
        type: 'range',
        name: 'amplitude',
        label: 'Wave Amplitude',
        description: 'Height of the waves',
        min: 0.01,
        max: 0.5,
        step: 0.01,
        defaultValue: 0.1,
        colspan: 2, // Full width for this important control
      },
      {
        type: 'range',
        name: 'frequency',
        label: 'Wave Frequency',
        description: 'Number of waves across the surface',
        min: 1,
        max: 50,
        step: 0.5,
        defaultValue: 10.0,
        colspan: 1,
      },
      {
        type: 'range',
        name: 'steepness',
        label: 'Wave Steepness',
        description: 'How steep the waves are',
        min: 0.1,
        max: 1.5,
        step: 0.1,
        defaultValue: 0.5,
        colspan: 1,
      },
      {
        type: 'color',
        name: 'color',
        label: 'Water Color',
        description: 'Color of the water',
        defaultValue: '#0077ff',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color',
        defaultValue: '#000000',
        colspan: 1,
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.TEXTURE,
      category: ShaderCategory.SIMULATIONS,
      description: 'Realistic water wave simulation with Gerstner waves',
      tags: ['water', 'waves', 'ocean', 'simulation', 'realistic'],
    };
  }
}

// 2. WaterWavePreview Component
export const WaterWavePreview: React.FC<{ model: WaterWaveShader }> = ({
  model,
}) => {
  const getUniformNames = useCallback(
    () => [
      'u_color',
      'u_background_color',
      'u_amplitude',
      'u_frequency',
      'u_speed',
      'u_steepness',
    ],
    []
  );

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: WaterWaveShader
    ) => {
      // Update shader-specific uniforms
      gl.uniform1f(locations.u_amplitude, model.amplitude);
      gl.uniform1f(locations.u_frequency, model.frequency);
      gl.uniform1f(locations.u_speed, model.speed);
      gl.uniform1f(locations.u_steepness, model.steepness);

      // Update color uniforms
      const colorVec3 = hexToVec3(model.color);
      const backgroundColorVec3 = hexToVec3(model.backgroundColor);

      gl.uniform3fv(locations.u_color, colorVec3);
      gl.uniform3fv(locations.u_background_color, backgroundColorVec3);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return <canvas ref={canvasRef} width={model.width} height={model.height} />;
};
